import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormDescription,
  FormMessage,
} from "@/components/ui/form";
import {
  CricketPredictionUpdatePoints,
  GetCricketMatchPoints,
} from "@services/CricketPrediction";
import { useMutation, useQuery } from "@tanstack/react-query";
import { showToast } from "@utils/helper/index.helper";
import { Button } from "@components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@components/ui/dialog";
import { Input } from "@components/ui/input";
import { z } from "zod";
import { useEffect } from "react";

const pointsSchema = z.object({
  toss_winner_point: z.coerce.number().min(1, "Points must greater than 0"),
  match_winner_point: z.coerce.number().min(1, "Points must greater than 0"),
  player_of_the_match_point: z.coerce
    .number()
    .min(1, "Points must greater than 0"),
});

type PointsFormValues = z.infer<typeof pointsSchema>;

function ManagePoints({ setIsModalOpen, refetch, isModalOpen }: any) {
  const { data: ManagePointsFetchedData } = useQuery({
    queryKey: ["manage-points-fetched-data"],
    queryFn: () => GetCricketMatchPoints(),
  });

  console.log("this is manage-points-data", ManagePointsFetchedData?.data);

  const { mutate: updatePoints, isPending: isUpdating } = useMutation({
    mutationFn: CricketPredictionUpdatePoints,
    onSuccess: () => {
      showToast({
        title: "Success",
        description: "Points updated successfully",
        type: "success",
      });
      setIsModalOpen(false);
      refetch();
    },
    onError: () => {
      showToast({
        title: "Error",
        description: "Failed to update points",
        type: "error",
      });
    },
  });

  const form = useForm<PointsFormValues>({
    resolver: zodResolver(pointsSchema),
    defaultValues: {
      toss_winner_point: 0,
      match_winner_point: 0,
      player_of_the_match_point: 0,
    },
  });

  const onSubmit = (data: PointsFormValues) => {
    updatePoints(data);
  };

  useEffect(() => {
    if (ManagePointsFetchedData?.data) {
      form.reset({
        ...ManagePointsFetchedData?.data,
      });
    }
  }, [ManagePointsFetchedData?.data]);

  return (
    <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Manage Points</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 py-4"
          >
            {/* Toss Points */}
            <FormField
              control={form.control}
              name="toss_winner_point"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Add Toss Winner Points</FormLabel>
                  <FormDescription>
                    Choose points for correct toss prediction.
                  </FormDescription>
                  <FormControl>
                    <Input type="number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Match Points */}
            <FormField
              control={form.control}
              name="match_winner_point"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Add Match Winner Points</FormLabel>
                  <FormDescription>
                    Choose points for correct match winner.
                  </FormDescription>
                  <FormControl>
                    <Input type="number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* POM Points */}
            <FormField
              control={form.control}
              name="player_of_the_match_point"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Add Player of Match Points</FormLabel>
                  <FormDescription>
                    Choose points for POM prediction.
                  </FormDescription>
                  <FormControl>
                    <Input type="number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="pt-4">
              <Button
                type="submit"
                className="w-full bg-[#1DA077] hover:bg-[#1DA077]/90"
                disabled={isUpdating}
              >
                {isUpdating ? "Applying..." : "Apply"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default ManagePoints;
